import {
  addContentTypesToQuery,
  addMetadataToQuery,
  relativeQuery,
  withQuerystringResults,
} from 'govbr.frontend.policy/helpers';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { compose } from 'redux';

import PersonCard from '../../PersonCard/PersonCard';

import AreaTreeNode from './AreaTreeNode';
import styles from './ListingPersons.module.less';
import { personsGridClass } from './utils';

// Profundidade máxima da árvore (alterar aqui para ajustar alcance da busca).
const TREE_MAX_DEPTH = 100;

const getContentTypes = (data = {}) => {
  return data?.showPersons === false ? ['Area'] : ['Area', 'Pessoa'];
};

const getMetadataFields = (data = {}) => {
  const showPersons = data?.showPersons !== false;
  const showAreasDescriptions = data?.showAreasDescriptions !== false;
  const personsShowDescription = !!data?.personsShowDescription;
  const summaryCard = !!data?.summaryCard;
  const showPersonsAllLinks = !!data?.showPersonsAllLinks;
  const showPersonsSubstitute = !!data?.showPersonsSubstitute;

  const metadataFields = ['title', 'review_state'];

  if (showAreasDescriptions) {
    metadataFields.push('address_and_hours', 'competencies', 'description');
  }

  if (!showPersons) return metadataFields;

  metadataFields.push(
    'agenda',
    'cargo',
    'image',
    'url',
    'pessoa_historico_agenda_externa',
    'halign',
    'valign',
  );

  if (personsShowDescription) {
    metadataFields.push('description');
  }

  if (!summaryCard) {
    metadataFields.push('email', 'telefone');
  }

  if (!summaryCard && showPersonsAllLinks) {
    metadataFields.push('file', 'url_curriculo', 'pessoa_historico_agenda');
  }

  if (showPersonsSubstitute) {
    metadataFields.push('substituto');
  }

  return Array.from(new Set(metadataFields));
};

const getQuery = ({ path, b_size, props }) => {
  const contentTypes = getContentTypes(props?.data);
  const metadataFields = getMetadataFields(props?.data);

  return compose(
    (query) => addContentTypesToQuery(query, contentTypes),
    (query) => addMetadataToQuery(query, metadataFields),
    makeDeepRelativeQuery(relativeQuery),
  )({ path, b_size });
};

// Versão profunda da relativeQuery para trazer toda a árvore em uma única chamada.
// Usa constante TREE_MAX_DEPTH para ajustar a profundidade da busca.
const makeDeepRelativeQuery = (orig) => (params) => {
  const q = orig(params);
  return {
    ...q,
    b_size: 10000000000,
    limit: 10000000000,
    query: q.query.map((cond) =>
      cond.i === 'path' &&
      cond.o === 'plone.app.querystring.operation.string.relativePath'
        ? { ...cond, ...(TREE_MAX_DEPTH ? { v: `::${TREE_MAX_DEPTH}` } : {}) }
        : cond,
    ),
  };
};

// (Wrapper AreaTree removido para evitar omissão de props; usamos AreaTreeNode diretamente.)

const messages = defineMessages({
  emptyArea: {
    id: 'listing.persons.empty',
    defaultMessage:
      'This area does not have people or subordinate areas registered yet',
  },
  whoIsWho: {
    id: 'listing.persons.whoiswho',
    defaultMessage: 'Who is who',
  },
  areaPeople: {
    id: 'listing.persons.areapeople',
    defaultMessage:
      '{count, plural, one {Person in this area} other {People in this area}}',
  },
});

const ListingPersonsBodyBase = (props) => {
  const {
    listingItems = [],
    isEditMode,
    data,
    path: basePath,
    hasLoaded,
    isLoading,
    error,
  } = props;
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);

  const getType = (item) => item?.portal_type || item?.['@type'];
  // Normaliza basePath para remover barra final, se existir (exceto se for apenas "/")
  const normalizedBasePath =
    basePath && basePath !== '/' ? basePath.replace(/\/+$/, '') : basePath;

  // Áreas raiz: apenas filhos diretos (exclui a própria área base para evitar duplicação visual)
  const rootAreas = listingItems.filter((item) => {
    if (getType(item) !== 'Area') return false;
    if (!normalizedBasePath) return true; // fallback
    const id = item['@id'];
    if (id === normalizedBasePath) return false; // área base tratada separadamente
    if (!id.startsWith(normalizedBasePath + '/')) return false;
    const rel = id.slice(normalizedBasePath.length + 1);
    return !rel.includes('/');
  });

  const personsPerRow = data?.personsPerRow || '3';
  const personsShowDescription = !!data?.personsShowDescription;
  const showPersons = data?.showPersons !== false;
  const summaryCard = !!data?.summaryCard;
  const peopleLinks = data?.peopleLinks !== false;
  const showAreasDescriptions = data?.showAreasDescriptions !== false;
  const showPersonsAllLinks = !!data?.showPersonsAllLinks;
  const showPersonsSubstitute = !!data?.showPersonsSubstitute;

  // Pessoas diretamente na raiz (filhos diretos do basePath)
  const rootPersons = showPersons
    ? listingItems.filter((item) => {
        if (getType(item) !== 'Pessoa') return false;
        if (!normalizedBasePath) return false;
        const id = item['@id'];
        if (!id.startsWith(normalizedBasePath + '/')) return false;
        const rel = id.slice(normalizedBasePath.length + 1);
        return !rel.includes('/');
      })
    : [];

  const { description, competencies, address_and_hours } = props.content;
  const hasAreaInfo = !!description || !!competencies || !!address_and_hours;

  return (
    <div className="listing-persons-block">
      {!!token && rootAreas.length === 0 && rootPersons.length === 0 && (
        <div className="text-danger text-center">
          {intl.formatMessage(messages.emptyArea)}
        </div>
      )}

      {/* Pessoas diretamente na raiz */}
      {showPersons && rootPersons.length > 0 && (
        <div className="root-persons smb-4">
          {showAreasDescriptions && (
            <h2 className="text-weight-medium text-up-02 mb-3">
              {intl.formatMessage(messages.areaPeople, {
                count: rootPersons.length,
              })}
            </h2>
          )}
          <div className={personsGridClass(personsPerRow, styles)}>
            {rootPersons.map((person) => (
              <PersonCard
                key={person['@id']}
                person={person}
                showDescription={personsShowDescription}
                showPersonLink={peopleLinks}
                summary={summaryCard}
                showAllLinks={showPersonsAllLinks}
                showSubstitute={showPersonsSubstitute}
                isEditMode={isEditMode}
              />
            ))}
          </div>
        </div>
      )}

      {rootAreas.length > 0 && (
        <>
          {hasAreaInfo || rootPersons.length > 0 ? (
            <h2 className="text-weight-medium text-up-02 mb-1">
              Áreas subordinadas
            </h2>
          ) : null}
          {rootAreas.map((area) => (
            <AreaTreeNode
              key={area['@id']}
              id={`node:${area['@id']}`}
              path={area['@id']}
              title={area.title}
              area={area}
              depth={0}
              personsPerRow={personsPerRow}
              personsShowDescription={personsShowDescription}
              showPersons={showPersons}
              summaryCard={summaryCard}
              peopleLinks={peopleLinks}
              showAreasDescriptions={showAreasDescriptions}
              showPersonsAllLinks={showPersonsAllLinks}
              showPersonsSubstitute={showPersonsSubstitute}
              listingItems={listingItems}
              hasLoaded={hasLoaded}
              isLoading={isLoading}
              error={error}
              isEditMode={isEditMode}
            />
          ))}
        </>
      )}
    </div>
  );
};

const ListingPersonsBody = withQuerystringResults(
  ListingPersonsBodyBase,
  getQuery,
);

ListingPersonsBody.propTypes = {
  isEditMode: PropTypes.bool,
};

export default ListingPersonsBody;
